import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

// Re-export Section for backward compatibility
export type { Section };

export type CustomerFeatureFlagDefinitionSchema = BaseFeatureFlagDefinition & {
  section?: Section;
};

export const CustomerFeatureFlagDataZod = zod.object({
  classifyStep: zod.boolean().default(false),
  locateStep: zod.boolean().default(false),
  ros2: zod.boolean().default(true),
  legacyHaasSteps: zod.boolean().default(true),
  enableCodeBlocks: zod.boolean().default(false),
  pythonExpressions: zod.boolean().default(false),
  etherNetIP: zod.boolean().default(true),
  darkMode: zod.boolean().default(false),
  useTeachPendant: zod.boolean().default(true),
});

export type CustomerFeatureFlagData = zod.infer<
  typeof CustomerFeatureFlagDataZod
>;
export type CustomerFeatureFlagKey = keyof CustomerFeatureFlagData;

export const customerFeatureFlagDefinitions: Record<
  CustomerFeatureFlagKey,
  CustomerFeatureFlagDefinitionSchema
> = {
  classifyStep: {
    title: 'Classify Step',
    section: 'vision',
    description: 'Classify/Categorize what the camera sees',
    allowSingleUser: true,
  },
  ros2: {
    title: 'Enable ROS2',
    description: 'Enable ROS2 motions and migrations',
    section: 'movement',
    allowSingleUser: false,
  },
  locateStep: {
    title: 'Locate Step',
    section: 'vision',
    description: 'Find objects in 3D space',
    allowSingleUser: true,
  },
  legacyHaasSteps: {
    title: 'Legacy Haas steps',
    description:
      'Include the legacy Haas steps "Monitor Haas machine" and "Run Haas program" in the step library',
    section: 'equipment',
    allowSingleUser: true,
  },
  enableCodeBlocks: {
    title: 'Enable Code Blocks',
    description: 'Enable Code Blocks',
    section: 'infra',
  },
  pythonExpressions: {
    title: 'Python Expressions',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable python expressions',
  },
  etherNetIP: {
    title: 'EtherNet/IP',
    section: 'equipment',
    allowSingleUser: false,
    description: 'Enable EtherNet/IP adapter integration',
  },
  darkMode: {
    title: 'Dark mode',
    section: 'infra',
    allowSingleUser: true,
    description: 'Enable dark mode UI',
  },
  useTeachPendant: {
    title: 'Use Teach Pendant 3PE',
    description: "Enable use of the teach pendant's 3PE safety stop",
    section: 'safety',
    allowSingleUser: false,
  },
};

type GroupedKeys = { section: Section; keys: CustomerFeatureFlagKey[] }[];

export const customerFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(customerFeatureFlagDefinitions)) {
    const key = keyStr as CustomerFeatureFlagKey;
    let { section } = customerFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
